import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Button, IconButton, Typography, useMediaQuery } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { useStateContext } from "contexts/ContextProvider";

// Spotlight overlay stacking: above app chrome (navbar appBar 1100, sidebar
// nav Box 1200, temporary Drawer modal 1200) and BELOW the app's global
// ContextProvider dialog — components/Dialog.jsx is a plain MUI Dialog with no
// zIndex override, i.e. theme.zIndex.modal = 1300 — so app dialogs (network
// notice etc.) still render above the tour if one fires mid-tour.
const TOUR_OVERLAY_Z_INDEX = 1250;

// Spotlight cutout shade (single-element technique; softer than the Overlay
// shell's 0.8 — the tour invites, the overlay locks).
const TOUR_SHADE = "rgba(0, 0, 0, 0.72)";

// Breathing room between the target's bounding rect and the spotlight ring (px).
const SPOTLIGHT_PADDING = 6;

// Viewport margin kept clear for the text card + gap spotlight→card (px).
const CARD_MARGIN = 12;
const CARD_GAP = 16;

// Re-measure burst after each step change — covers the mobile drawer slide-in
// (~225ms) and the smooth scrollIntoView settle. Chosen strategy: measure
// immediately, re-measure on this burst, and recompute on every resize/scroll
// (rAF-throttled) — no fixed settle wait.
const REMEASURE_DELAYS_MS = [0, 80, 180, 320, 500, 650];

// Measure an anchored target and return its padded spotlight rect, or null
// when the anchor is absent or zero-sized (→ centered fallback card).
const measureAnchor = (tourId) => {
  if (!tourId) return null;
  const element = document.querySelector(`[data-tour-id="${tourId}"]`);
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return null;
  return {
    top: rect.top - SPOTLIGHT_PADDING,
    left: rect.left - SPOTLIGHT_PADDING,
    width: rect.width + SPOTLIGHT_PADDING * 2,
    height: rect.height + SPOTLIGHT_PADDING * 2,
  };
};

// Place the card NEXT TO the target: prefer right, then below, then above,
// then left; final fallback clamps a below-placement; everything stays inside
// the viewport with 12px margins. No spotlight rect → centered card.
const computeCardPosition = (spotlightRect, cardWidth, cardHeight) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  if (!spotlightRect) {
    return {
      top: Math.max(CARD_MARGIN, (viewportHeight - cardHeight) / 2),
      left: Math.max(CARD_MARGIN, (viewportWidth - cardWidth) / 2),
    };
  }

  const maxLeft = Math.max(CARD_MARGIN, viewportWidth - cardWidth - CARD_MARGIN);
  const maxTop = Math.max(CARD_MARGIN, viewportHeight - cardHeight - CARD_MARGIN);
  const clampedTop = clamp(spotlightRect.top, CARD_MARGIN, maxTop);
  const clampedLeft = clamp(spotlightRect.left, CARD_MARGIN, maxLeft);

  const spaceRight =
    viewportWidth - (spotlightRect.left + spotlightRect.width) - CARD_GAP - CARD_MARGIN;
  const spaceBelow =
    viewportHeight - (spotlightRect.top + spotlightRect.height) - CARD_GAP - CARD_MARGIN;
  const spaceAbove = spotlightRect.top - CARD_GAP - CARD_MARGIN;
  const spaceLeft = spotlightRect.left - CARD_GAP - CARD_MARGIN;

  if (spaceRight >= cardWidth) {
    return { top: clampedTop, left: spotlightRect.left + spotlightRect.width + CARD_GAP };
  }
  if (spaceBelow >= cardHeight) {
    return { top: spotlightRect.top + spotlightRect.height + CARD_GAP, left: clampedLeft };
  }
  if (spaceAbove >= cardHeight) {
    return { top: spotlightRect.top - cardHeight - CARD_GAP, left: clampedLeft };
  }
  if (spaceLeft >= cardWidth) {
    return { top: clampedTop, left: spotlightRect.left - cardWidth - CARD_GAP };
  }
  // No side fits (very small viewport): place below and clamp inside it.
  return {
    top: clamp(spotlightRect.top + spotlightRect.height + CARD_GAP, CARD_MARGIN, maxTop),
    left: clampedLeft,
  };
};

// Screen-reader-only prefix inside the step heading (spec §8).
const visuallyHiddenSx = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: -1,
  border: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

// Copy deck (spec §7) — strings verbatim; the spotlight rebuild changed layout
// only, not wording. `anchor` resolves the data-tour-id of the real control
// this step points at (null → centered fallback card, no spotlight).
// body/caption take isDesktopNav (min-width:900px) — the same boundary the
// navbar uses for the bell vs avatar-menu variants.
const STEPS = [
  {
    key: "dashboard",
    title: "Your Dashboard",
    anchor: () => "nav-dashboard",
    body: () =>
      "Welcome! This is the first screen you see after login — your upcoming assignments and updates appear here.",
    caption: (isDesktopNav) =>
      isDesktopNav ? "Menu → Dashboard" : "Menu (top left) → Dashboard",
  },
  {
    key: "interpretations",
    title: "Your Interpretations",
    anchor: () => "nav-interpretations",
    body: () =>
      "All of your interpretation jobs live here. Open a job to see its details, schedule, and documents.",
    caption: (isDesktopNav) =>
      isDesktopNav ? "Menu → Interpretations" : "Menu (top left) → Interpretations",
  },
  {
    key: "alerts",
    title: "Your Alerts",
    anchor: (isDesktopNav) => (isDesktopNav ? "nav-alerts" : "nav-avatar"),
    body: (isDesktopNav) =>
      isDesktopNav
        ? "Tap the bell at the top right to see your alerts. A red number on it means you have unread ones."
        : "Open your account menu at the top right, then tap Notifications to see your alerts. A red number means you have unread ones.",
    caption: (isDesktopNav) =>
      isDesktopNav
        ? "Bell icon — top right of the screen"
        : "Account menu — top right of the screen",
  },
  {
    key: "profile",
    title: "Your Profile Settings",
    anchor: () => "nav-profile",
    body: () =>
      "Keep your contact details, languages, and notification preferences up to date here.",
    caption: (isDesktopNav) =>
      isDesktopNav ? "Menu → Profile Settings" : "Menu (top left) → Profile Settings",
  },
  {
    key: "permission",
    title: "Turn On Alerts",
    anchor: () => null,
    body: () =>
      "Next, your browser will show a permission pop-up. Click Allow so you never miss an assignment offer.",
    caption: () => "You can change this anytime in Profile Settings.",
  },
];

const LinguistOnboardingTour = ({ open, onClose }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [cardPosition, setCardPosition] = useState(null);
  const headingRef = useRef(null);
  const cardRef = useRef(null);
  // Tracks whether THIS tour session opened the mobile drawer so it knows to
  // close it again (sidebar steps need the drawer as the spotlit surface).
  const openedDrawerRef = useRef(false);
  const isDesktopNav = useMediaQuery("(min-width:900px)");
  const { setActiveMenu } = useStateContext();

  const step = STEPS[stepIndex];
  const stepKey = step.key;
  const anchorId = step.anchor(isDesktopNav);
  const isLastStep = stepIndex === STEPS.length - 1;

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  // Focus the step heading on open AND on every step change (spec §8).
  useEffect(() => {
    if (!open) return;
    const heading = headingRef.current;
    if (heading) heading.focus();
  }, [open, stepIndex]);

  // Escape closes AND counts as a dismissal (same semantics as Skip → seen).
  // The shade itself is NOT dismissable — same rule as the Dialog's ignored
  // backdrop (spec §8); the overlay swallows mis-clicks so the app can never
  // be operated mid-tour.
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Mobile (<900px): the sidebar nav lives in a Drawer that is closed on entry
  // (LinguistPortalLayout forces setActiveMenu(false) at screenSize <= 900).
  // Open it for sidebar-target steps through the SAME context state the
  // sidebar reads — SidebarLinguist renders `{activeMenu && <Drawer
  // variant={!isNonMobile ? "temporary" : "permanent"}>}` — and close it again
  // when leaving those steps, on close, or on unmount.
  useEffect(() => {
    if (!open) return undefined;
    const isSidebarStep = ["dashboard", "interpretations", "profile"].includes(stepKey);
    if (isSidebarStep && !isDesktopNav) {
      openedDrawerRef.current = true;
      setActiveMenu(true);
    } else if (openedDrawerRef.current) {
      openedDrawerRef.current = false;
      setActiveMenu(false);
    }
    return undefined;
  }, [open, stepKey, isDesktopNav, setActiveMenu]);

  useEffect(() => {
    if (open) return undefined;
    if (openedDrawerRef.current) {
      openedDrawerRef.current = false;
      setActiveMenu(false);
    }
    return undefined;
  }, [open, setActiveMenu]);

  // Measure the anchor: immediately on step change, again on the burst
  // schedule (drawer slide-in ≈225ms, smooth scrollIntoView settle) and on
  // every resize/scroll (rAF-throttled, capture:true so inner scroll
  // containers count too).
  useEffect(() => {
    if (!open) return undefined;
    let disposed = false;
    let frame = 0;
    const timeouts = [];

    const applyMeasure = () => {
      if (disposed) return null;
      const rect = anchorId ? measureAnchor(anchorId) : null;
      setSpotlightRect(rect);
      return rect;
    };

    // Scroll the anchor into view first (no-op when already visible) — the
    // burst + scroll listener re-measure while smooth scrolling settles.
    if (anchorId) {
      const element = document.querySelector(`[data-tour-id="${anchorId}"]`);
      if (element && typeof element.scrollIntoView === "function") {
        element.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
    applyMeasure();

    REMEASURE_DELAYS_MS.forEach((delay) => {
      timeouts.push(
        window.setTimeout(() => {
          applyMeasure();
        }, delay),
      );
    });

    const scheduleMeasure = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        applyMeasure();
      });
    };
    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("scroll", scheduleMeasure, true);

    return () => {
      disposed = true;
      timeouts.forEach((id) => window.clearTimeout(id));
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure, true);
    };
  }, [open, stepIndex, anchorId]);

  // Position the card next to the spotlight (right → below → above → left,
  // always clamped inside the viewport). useLayoutEffect = measured and
  // re-clamped before paint, so a new step never flashes at a stale spot.
  useLayoutEffect(() => {
    if (!open) return;
    const card = cardRef.current;
    const cardWidth = card ? card.offsetWidth : 320;
    const cardHeight = card ? card.offsetHeight : 240;
    setCardPosition(computeCardPosition(spotlightRect, cardWidth, cardHeight));
  }, [open, stepIndex, spotlightRect, isDesktopNav]);

  const handleNext = () => {
    setStepIndex((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  const handleAllow = () => {
    // onClose FIRST: the layout synchronously writes the seen key, so no
    // navbar-initiated call can slip in front of the real prompt (spec §4.2).
    onClose();
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  };

  if (!open) return null;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: TOUR_OVERLAY_Z_INDEX }}>
      {spotlightRect ? (
        // Single-element spotlight: the target's padded rect with a 2px teal
        // ring + 9999px shade cutout (no four-panel math), animated between
        // steps. The overlay root swallows clicks outside the card, so the
        // app underneath can never be operated mid-tour.
        <Box
          aria-hidden="true"
          sx={(theme) => ({
            position: "fixed",
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            borderRadius: "12px",
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}, 0 0 0 9999px ${TOUR_SHADE}`,
            transition: theme.transitions.create(["top", "left", "width", "height"], {
              easing: theme.transitions.easing.easeInOut,
              duration: 200,
            }),
          })}
        />
      ) : (
        <Box
          aria-hidden="true"
          sx={{ position: "fixed", inset: 0, bgcolor: TOUR_SHADE }}
        />
      )}

      <Box
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="linguist-tour-step-title"
        sx={{
          position: "fixed",
          ...(cardPosition
            ? { top: cardPosition.top, left: cardPosition.left }
            : { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }),
          width: 320,
          maxWidth: "calc(100vw - 24px)",
          p: 2,
          pt: 2.5,
          borderRadius: "12px",
          bgcolor: "background.default",
          boxShadow: (theme) => theme.shadows[8],
          textAlign: "center",
          transition: (theme) =>
            theme.transitions.create(["top", "left"], {
              easing: theme.transitions.easing.easeInOut,
              duration: 200,
            }),
        }}
      >
        <IconButton
          aria-label="Close tour"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            width: 40,
            height: 40,
            color: "text.secondary",
          }}
        >
          <CloseRounded sx={{ fontSize: 20 }} />
        </IconButton>

        <Typography
          aria-hidden="true"
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            fontWeight: 600,
            letterSpacing: "0.04em",
            mb: 0.5,
          }}
        >
          Step {stepIndex + 1} of {STEPS.length}
        </Typography>

        <Typography
          ref={headingRef}
          tabIndex={-1}
          id="linguist-tour-step-title"
          variant="h4"
          sx={{ mb: 0.75, outline: "none" }}
        >
          <Box component="span" sx={visuallyHiddenSx}>
            Step {stepIndex + 1} of 5:{" "}
          </Box>
          {step.title}
        </Typography>

        <Typography variant="body1" sx={{ mb: 1 }}>
          {step.body(isDesktopNav)}
        </Typography>

        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", mb: 2 }}
        >
          {step.caption(isDesktopNav)}
        </Typography>

        <Box
          aria-hidden="true"
          sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 1.5 }}
        >
          {STEPS.map((entry, index) => (
            <Box
              key={entry.key}
              sx={(theme) => ({
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor:
                  index === stepIndex
                    ? "primary.main"
                    : theme.palette.mode === "dark"
                      ? "grey.700"
                      : "grey.300",
              })}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: isLastStep ? "flex-end" : "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            rowGap: 1,
            pt: 0.5,
          }}
        >
          {!isLastStep && (
            <Button
              variant="text"
              onClick={onClose}
              sx={{ color: "text.secondary", minHeight: 40, px: 2.5 }}
            >
              Skip tour
            </Button>
          )}

          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            {stepIndex > 0 && (
              <Button
                variant="text"
                onClick={handleBack}
                sx={{ minHeight: 40, px: 2.5 }}
              >
                Back
              </Button>
            )}

            {isLastStep ? (
              <>
                <Button
                  variant="text"
                  onClick={onClose}
                  sx={{ color: "text.secondary", minHeight: 40, px: 2.5 }}
                >
                  Not now
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAllow}
                  sx={{ minHeight: 40, px: 2.5 }}
                >
                  Allow notifications
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ minHeight: 40, px: 2.5 }}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LinguistOnboardingTour;
